import { default as _log } from '@dw/loglevel';
import Query from "./query.js";

// lodash 
import find from "lodash-es/find";
import isEqual from "lodash-es/isEqual";
import get from 'lodash-es/get';

export default class QueryById extends Query {

  constructor({ couchdb, database, queryId, docId, localOnly, once, dispatch }) {
    super({ couchdb, database, queryId, localOnly, once, dispatch });

    this.docId = docId;
    this._onLocalDbChanges = this._onLocalDbChanges.bind(this);
    // logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.database.dbName}.QueryById.${this.queryId}`;

    //set logger
    this.log = _log.getLogger(`dreamdb.queryById.${this.couchdb.name}.${this.database.dbName}.${this.queryId}`);
    this._dispatchQuery();
    this._startQuery();
  }

  /**
   * Returns serialized format of the query. e.g. `{id:"", type:"BY_ID", docId: ""}`
   */
  get query() {
    return {
      id: this.queryId,
      type: 'BY_ID',
      docId: this.docId,
      localOnly: this.localOnly,
      once: this.once
    };
  }

  /**
   * This method starts reading query in local database as well as on remote database.
   */
  async _startQuery() {
    this._setMetadata({ lastQueryTime: new Date().getTime() });

    if (this.once) {
      this._readDocumentOnce();
      return;
    }

    this._readLocalDocument();
    if (!this.localOnly) {
      this._readRemoteDocument();
    }
  }

  /**
   * It reads document only one time from remote and local db.
   * If `localOnly` is true, then it will read document only in local database.
   */
  async _readDocumentOnce() {
    //Perform only on local database
    if (this.localOnly) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true });
      return;
    }

    //Perform query on both remote database and local database
    let response = await this.database.remoteDb.queryByIds({ queryId: this.queryId, docIds: [this.docId], once: this.once });

    if (response.success) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true, inSync: true });
      return;
    }

    this.log.error(`${this.logPrefix}._readDocumentOnce(): Failed to read document from remote db::`, response.error);
    this._rejectVal(response.error);
    this._setMetadata({ localInSync: true, inSync: true, error: response.error });
  }

  /**
   * It fetches document from the local database.
   * @returns {Promise} Returns promise which resolves with query result fetched
   */
  async _fetchLocalDocument() {
    return await this.database.localDb.fetch(this.docId);
  }

  /**
   * It parses the query result
   * 
   * @param {Object} response query response
   * @return {Object/null} Return document if available. returns null if response has error or document is deleted
   */
  _parseQuery(response) {
    if (response.error === "not_found") {
      return null;
    }

    if(response.value && response.value.deleted) {
      return {
        _id: response.id,
        _rev: response.value.rev,
        _deleted: true
      };
    }

    return response.doc;
  }

  /**
   * It reads document from the remote db.
   */
  async _readRemoteDocument() {
    let response = await this.database.remoteDb.queryByIds({ queryId: this.queryId, docIds: [this.docId], once: this.once });

    if (response.success) {
      if (this.value === undefined) {
        // When document not found from local database then we re-runs the query in local database after remote query
        // is completed to prevents extra `null` value snapshot.
        await this._performLocalQuery();
      }

      this._setMetadata({ inSync: true });
      this._resolveVal(this.value);
      return;
    }

    this._rejectVal(response.error);
    this.log.error(`${this.logPrefix}._readRemoteDocument(): Failed to read document from remote db ::`, response.error);
    this._setMetadata({ inSync: true, error: response.error });
  }

  /**
   * It fetches the document from the local db and set value
   */
  async _performLocalQuery() {
    let response = await this._fetchLocalDocument();
    this.log.debug(`${this.logPrefix}._performLocalQuery(): document is read from localDb ::`, response);
    let value = this._parseQuery(response);

    if (!isEqual(this.value, value)) {
      this._setValue(value);
    }
  }

  /**
   * This method is used to read document from local database.
   * listens `changes` feed of the localDb
   *  - If record is not found, do nothing
   *  - If record is deleted, set value as `null`.
   *  - If record is found, set value.
   */
  async _readLocalDocument() {
    let response = await this._fetchLocalDocument();
    this._watchLocalDbChanges();

    if (response.error) {
      if (this.localOnly) {
        this._setValue(null);
        this._resolveVal(null);
      }
      this._setMetadata({ localInSync: true });
      return;
    }

    let value = this._parseQuery(response);
    this._setValue(value);
    this._setMetadata({ localInSync: true });

    if (this.localOnly) {
      this._resolveVal(this.value);
    }
  }

  /**
   * This method called on localDb `changes` event. Updates value if requested doc is changed
   * @param {Array} Array of changed documents in local database
   */
  _onLocalDbChanges(docs) {
    let changedDoc = find(docs, { id: this.docId });

    if (!changedDoc) {
      return;
    }

    this.log.info(`${this.logPrefix}._onLocalDbChanges(): document is updated`, changedDoc.doc);

    let value = changedDoc.doc;

    if(!this.value && value.deleted) {
      let doc = {
        _id: value._id,
        _rev: value._rev,
        _deleted: true
      };

      this._setValue(doc);
      return;
    }

    //update value if its actually changed
    if(get(this.value, '_rev') !== get(value, '_rev')) {
      this._setValue(value);
    }
  }

}
